import React, { useState } from "react";
import { api } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { useSeo } from "../components/site/Seo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { track } from "../lib/tracking";
import { MessageCircle, Mail, Clock } from "lucide-react";

export default function Contact() {
  const { settings } = useSite();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  useSeo({ title: `Contact | ${settings.brand_name || "Agni Shiksha"}`, description: "Get in touch with the Agni Shiksha support team." });
  const wa = (settings.whatsapp || "").replace(/\D/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      track("Lead", { source: "contact_form" });
      toast.success("Thanks! We'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Could not send. Please try WhatsApp."); } finally { setBusy(false); }
  };

  return (
    <div className="container-px section-y max-w-5xl">
      <p className="text-xs uppercase tracking-widest text-ember mb-3">Contact</p>
      <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight mb-10">We're here to help</h1>
      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="card-surface p-6 space-y-4">
          <div><label className="text-sm text-content-secondary">Name</label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-ink border-line" data-testid="contact-name" /></div>
          <div><label className="text-sm text-content-secondary">Email</label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-ink border-line" data-testid="contact-email" /></div>
          <div><label className="text-sm text-content-secondary">Message</label>
            <Textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 bg-ink border-line" data-testid="contact-message" /></div>
          <Button disabled={busy} className="btn-ember rounded-full w-full h-11" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</Button>
        </form>
        <div className="space-y-4">
          {wa && (
            <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="card-surface p-5 flex items-center gap-4 hover:border-ember/60" data-testid="contact-whatsapp">
              <div className="w-11 h-11 rounded-xl bg-emerald-500/15 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-400" /></div>
              <div><div className="font-medium text-sm">WhatsApp</div><div className="text-sm text-content-secondary">{settings.whatsapp}</div></div>
            </a>
          )}
          <a href={`mailto:${settings.email}`} className="card-surface p-5 flex items-center gap-4 hover:border-ember/60">
            <div className="w-11 h-11 rounded-xl bg-ember-soft flex items-center justify-center"><Mail className="w-5 h-5 text-ember" /></div>
            <div><div className="font-medium text-sm">Email</div><div className="text-sm text-content-secondary">{settings.email}</div></div>
          </a>
          <div className="card-surface p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-card-hover flex items-center justify-center"><Clock className="w-5 h-5 text-content-secondary" /></div>
            <div><div className="font-medium text-sm">Support hours</div><div className="text-sm text-content-secondary">{settings.support_hours}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}
